import React from "react";
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  FormGroup,
  CircularProgress,
  Alert,
} from "@mui/material";
import { MaleIcon, FemaleIcon, OtherIcon } from "../../../../assets/icons";
import { useSexOptions } from "../hooks";

interface SexSelectProps {
  value: string[];
  onChange: (values: string[]) => void;
  label?: string;
}

const SEX_OPTIONS = [
  { label: "Male", value: "male", icon: <MaleIcon fontSize="small" /> },
  { label: "Female", value: "female", icon: <FemaleIcon fontSize="small" /> },
  { label: "Other", value: "other", icon: <OtherIcon fontSize="small" /> },
];

export const SexSelect: React.FC<SexSelectProps> = ({
  value,
  onChange,
  label = "Filter by Sex",
}) => {
  const { data, isLoading, error } = useSexOptions();
  const handleChange = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter((v) => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  if (error) {
    return (
      <Alert severity="error">
        Failed to load sex options. Please try again.
      </Alert>
    );
  }

  const optionMap = new Map(SEX_OPTIONS.map((option) => [option.value, option]));
  const options =
    data?.data
      ?.map((option) => optionMap.get(option.value))
      .filter((option): option is (typeof SEX_OPTIONS)[number] => Boolean(option)) ??
    SEX_OPTIONS;

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom color="text.secondary">
        {label}
      </Typography>
      {isLoading ? (
        <Box display="flex" alignItems="center" gap={1}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            Loading sex options...
          </Typography>
        </Box>
      ) : (
        <FormGroup row>
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  checked={value.includes(option.value)}
                  onChange={() => handleChange(option.value)}
                />
              }
              label={
                <Box display="flex" alignItems="center" gap={1}>
                  {option.icon}
                  <Typography variant="body2">{option.label}</Typography>
                </Box>
              }
            />
          ))}
        </FormGroup>
      )}
    </Box>
  );
};
